import { contextValues } from '@streem/logger';
import appLogger from '../../../util/logging/app_logger';
import { statusOptions, StatusOptionValueType } from '../status_option_value';
import { streem } from 'streem-sdk-protobuf';
import { defaultNotAvailableOption } from './default_availability_selection';
import { StreemApiUpdateUserAvailabilityRequestAvailabilityStatus } from '@streem/api';

const log = appLogger.extend('ExpertAvailabilityUtils');

const AvailabilityStatus = streem.api.AvailabilityStatus;

/**
 * Ensures we return an Option to display in the UI even if an issue occurs with some additional logging.
 * @param currentAvailabilityStatus the Streem API Availability Status enum
 * @returns the matched Availability Status option object
 */
export const findOptionThatMatchesWallStatus = (
    currentAvailabilityStatus: streem.api.AvailabilityStatus,
): StatusOptionValueType => {
    log.info(
        `Availability status from user wall: ${AvailabilityStatus[currentAvailabilityStatus]}`,
    );

    const matchedStatus = findOptionByWallStatus(currentAvailabilityStatus);

    log.setContextValue(contextValues.USER_AVAILABILITY, {
        status: AvailabilityStatus[currentAvailabilityStatus],
        label: matchedStatus?.label,
    });

    if (!matchedStatus) {
        log.info(
            `Matched AvailabilityStatus not found, meaning it is INVALID or OFFLINE. Defaulting selector to the NOT_AVAILABLE status`,
        );
        return defaultNotAvailableOption;
    } else {
        log.info(`Matched AvailabilityStatus: ${matchedStatus?.label}`);
        return matchedStatus;
    }
};

/**
 * A simple utility to match the Option keys with the Streem API Availability Status enum.
 * @param apiStatus Availability Status enum from the Streem API
 * @returns the matched Availability Status option object
 * @throws error with no matching status message
 */
export function getOptionFromApiStatus(
    apiStatus: streem.api.AvailabilityStatus,
): StatusOptionValueType {
    const matchingOption = statusOptions.find(option => option.value.keyArray.includes(apiStatus));

    if (!matchingOption) {
        throw new Error(`No matching status option found for API status: ${apiStatus}`);
    }

    return matchingOption;
}

/**
 * A consistent and simple way to match StatusOption from Streem API Availability Status
 * @param status the current status from Streem API
 * @param fallback (DEFAULT: false) if you don't want to handle it yourself, this defaults to NOT_AVAILABLE if set true
 * @returns the matched option or undefined
 */
export function findOptionByWallStatus(
    status: streem.api.AvailabilityStatus,
    fallback = false,
): StatusOptionValueType | undefined {
    // check the key array to ensure we're capturing all the scenarios in the Options
    const option = statusOptions.find(opt => opt.value.keyArray.includes(status));
    if (!option && fallback) {
        return defaultNotAvailableOption;
    }

    if (!option) {
        return undefined;
    }
    return option;
}

const availabilityStatusToApiRequestStatusMap: {
    [key in streem.api.AvailabilityStatus]: StreemApiUpdateUserAvailabilityRequestAvailabilityStatus;
} = {
    [AvailabilityStatus.AVAILABILITY_STATUS_INVALID]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.INVALID,
    [AvailabilityStatus.AVAILABILITY_STATUS_AVAILABLE]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.AVAILABLE,
    [AvailabilityStatus.AVAILABILITY_STATUS_NOT_AVAILABLE]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.NOTAVAILABLE,
    [AvailabilityStatus.AVAILABILITY_STATUS_RESERVED]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.RESERVED,
    [AvailabilityStatus.AVAILABILITY_STATUS_IN_CALL]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.INCALL,
    [AvailabilityStatus.AVAILABILITY_STATUS_CONFIRMING]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.CONFIRMING,
    [AvailabilityStatus.AVAILABILITY_STATUS_OFFLINE]:
        StreemApiUpdateUserAvailabilityRequestAvailabilityStatus.OFFLINE,
};

export function mapWallStatusToApiRequestStatus(
    status: streem.api.AvailabilityStatus,
): StreemApiUpdateUserAvailabilityRequestAvailabilityStatus {
    return availabilityStatusToApiRequestStatusMap[status];
}
